'use client';

import { useRouter, usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGettingStartedProgress } from '@/lib/hooks/use-getting-started-progress';
import { GettingStartedProgressRing } from '@/components/get-started/getting-started-progress-ring';

const HUB_PATH = '/onboarding/get-started';

/**
 * The sidebar's entry into the Getting Started hub, with a progress ring.
 *
 * **This is the app's permanent way into the checklist, and Dismiss no longer
 * hides it** (VALE-1830). It used to disappear the moment the user dismissed the
 * hub, which — together with the icon rail doing the same — left a dismissed
 * user no route back anywhere in the product, while the confirm dialog told them
 * to look in a "Help menu" that does not exist and the setup flow told them they
 * could "pick this up whenever you like from Getting started". Dismiss now means
 * only "don't open this on its own"; this button is what makes that safe.
 *
 * It is hidden for one reason only: the checklist does not apply to this user
 * (`canReach`, which is the sign-in prompt's own eligibility rule served by the
 * backend rather than transcribed here). A finished user keeps it, showing 6/6 —
 * it is also how they get back to the "Go further" material.
 */
export function GettingStartedWidget() {
  const router = useRouter();
  const pathname = usePathname();
  const { completedCount, status, canReach, isLoading } =
    useGettingStartedProgress();

  // Render nothing until the server has answered. There is no cached guess to
  // fall back on any more, and briefly showing a 0/6 ring to someone who has
  // finished is exactly the kind of small lie this ticket removes.
  if (isLoading || !canReach || !status) return null;

  const isActive = pathname?.startsWith(HUB_PATH);

  return (
    <Button
      variant="outline"
      size="default"
      className={cn(
        'hover:bg-accent hover:text-accent-foreground w-full justify-center gap-2 px-3 text-sm font-normal',
        isActive && 'bg-accent text-accent-foreground'
      )}
      onClick={() => router.push(HUB_PATH)}
    >
      <GettingStartedProgressRing
        completed={completedCount}
        total={status.totalCount}
        size={16}
      />
      Getting Started
    </Button>
  );
}
